///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Launch-time engagement bookkeeping for the account-conversion funnel:
/// records a session (at most one per local calendar day), detects deep-link
/// entry, refreshes the server-set anonymous id cookie, and emits the
/// session analytics event that funnel prompts will be tuned against.
///

import { useGetAnonymousSessionQuery } from "@/redux/engagementApi"
import { recordSession } from "@/redux/engagementSlice"
import { useAppDispatch, useAppStore } from "@/redux/store"
import { track } from "@/utils/analytics"
import { useEffect, useRef } from "react"

/**
 * A "deep link" entry is a shared view URL (camera/target/layers params) or
 * an arrival from another site. These sessions get gentler conversion
 * treatment: someone clicking a friend's hurricane link should not meet an
 * interstitial in their first minute.
 */
function detectDeepLinkEntry(arrivedViaShare: boolean): boolean {
	// Share links: useShareLaunch has already replaced ?s= with the
	// translated params by the time the app mounts, and a dataset share
	// translates to a bare URL - so the resolved share is the signal, not
	// the (now gone) ?s= param
	if (arrivedViaShare) {
		return true
	}
	const params = new URL(window.location.href).searchParams
	if (params.has("camera") || params.has("target") || params.has("layers") || params.has("s")) {
		return true
	}
	// Human-readable camera pose form (?lat=&lon=...)
	if (params.has("lat") && params.has("lon")) {
		return true
	}
	if (document.referrer) {
		try {
			return new URL(document.referrer).origin !== window.location.origin
		} catch {
			return false
		}
	}
	return false
}

export function useEngagementSession() {
	const dispatch = useAppDispatch()
	const store = useAppStore()
	const recorded = useRef(false)

	// Refresh (or mint) the durable eb_anon_id cookie once per launch
	useGetAnonymousSessionQuery()

	useEffect(() => {
		if (recorded.current) return
		recorded.current = true

		const isDeepLink = detectDeepLinkEntry(store.getState().dialog.activeShare !== null)
		dispatch(recordSession(isDeepLink))

		// Emitted after recordSession so the counts include this session
		const engagement = store.getState().engagement
		track("eb_session_started", {
			session_count: engagement.sessionCount,
			entered_via_deep_link: isDeepLink,
			first_session_was_deep_link: engagement.firstSessionWasDeepLink,
			days_since_first_seen: engagement.firstSeenAt
				? Math.floor((Date.now() - new Date(engagement.firstSeenAt).getTime()) / 86_400_000)
				: 0,
		})
	}, [dispatch, store])
}
